import {
  doc,
  setDoc,
  getDoc,
  getDocs,
  getFirestore,
  query,
  collection,
  orderBy,
  deleteDoc,
  where,
  onSnapshot,
  runTransaction,
  writeBatch,
  increment,
  collectionGroup,
  limit,
  startAfter,
  documentId,
} from "firebase/firestore";
import React, { useContext, useMemo, createContext, useCallback } from "react";

export const FirestoreContext = createContext({
  db: null,
});

export const FirestoreProvider = ({ children }) => {
  const db = useMemo(() => getFirestore(), []);

  return (
    <FirestoreContext.Provider
      value={{
        db,
      }}
    >
      {children}
    </FirestoreContext.Provider>
  );
};

export const useFirestore = () => {
  const { db } = useContext(FirestoreContext);

  const setFirestoreDoc = useCallback((collection, key, data, opts) => {
    return setDoc(doc(db, collection, key), data, opts);
  }, [db]);

  const getFirestoreDoc = useCallback((collection, key) => {
    return getDoc(doc(db, collection, key));
  }, [db]);

  const waitForDocUpdate = useCallback((collection, key, onChange) => {
    return new Promise((resolve, reject) => {
      const unsub = onSnapshot(
        doc(db, collection, key),
        (snapshot) => {
          onChange(snapshot, () => {
            unsub();
            resolve();
          });
        },
        (error) => {
          unsub();
          reject(error);
        }
      );
      setTimeout(() => {
        unsub();
        reject(new Error("wait timeout"));
      }, 600000);
    });
  }, [db]);

  const firestoreDoc = useCallback((collection, key) => {
    return doc(db, collection, key);
  }, [db]);

  const deleteFirestoreDoc = useCallback((collection, key) => {
    return deleteDoc(doc(db, collection, key));
  }, [db]);

  const getFirestoreDocs = useCallback((collectionName, ...queryArguments) => {
    return getDocs(query(collection(db, collectionName), ...queryArguments));
  }, [db]);

  const runFirestoreTransaction = useCallback((transaction) => {
    return runTransaction(db, transaction);
  }, [db]);

  const writeFirestoreBatch = useCallback(() => {
    return writeBatch(db);
  }, [db]);

  const getCollectionGroup = useCallback((collectionName, ...queryArguments) => {
    return getDocs(
      query(collectionGroup(db, collectionName), ...queryArguments)
    );
  }, [db]);

  return {
    db,
    orderBy,
    where,
    limit,
    startAfter,
    documentId,
    doc: firestoreDoc,
    setDoc: setFirestoreDoc,
    getDoc: getFirestoreDoc,
    getDocs: getFirestoreDocs,
    deleteDoc: deleteFirestoreDoc,
    waitForDocUpdate: waitForDocUpdate,
    runTransaction: runFirestoreTransaction,
    writeBatch: writeFirestoreBatch,
    increment: increment,
    getCollectionGroup,
  };
};
